import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, shareReplay, skip, first, firstValueFrom } from 'rxjs';
import { MediaDevicesService } from '@gatho/features/lobby/services/media/MediaDevicesService';
import { ActiveDataService } from '@gatho/features/lobby/services/data/ActiveDataService';
import { Area } from '@shared/models';


@Injectable({
  providedIn: 'root'
})
export class LocalMediaService {

  // Current state of media options
  // Most values here will have RXJS emmitters / observables

  // Track current state of video (enabled / disabled) and audio (muted / unmuted)
  public videoStatus: boolean = localStorage.getItem('videoStatus') !== null ? localStorage.getItem('videoStatus') === 'true' : true;
  private videoStatusSub = new BehaviorSubject<boolean>(this.videoStatus);
  public videoStatus$ = this.videoStatusSub.asObservable();

  public audioStatus: boolean = localStorage.getItem('audioStatus') !== null ? localStorage.getItem('audioStatus') === 'true' : true;
  private audioStatusSub = new BehaviorSubject<boolean>(this.audioStatus);
  public audioStatus$ = this.audioStatusSub.asObservable();

  public dndStatus: boolean = localStorage.getItem('dndStatus') !== null ? localStorage.getItem('dndStatus') === 'true' : false;
  private dndStatusSub = new BehaviorSubject<boolean>(this.dndStatus);
  public dndStatus$ = this.dndStatusSub.asObservable();

  public adminMuted: boolean = false;
  private adminMutedSub = new BehaviorSubject<boolean>(this.adminMuted);
  public adminMuted$ = this.adminMutedSub.asObservable();

  // Active stream objects
  // These are regular 'subjects' because they won't have an initial value we can use
  userStream?: MediaStream;
  userStreamSub = new Subject<MediaStream | undefined>();
  userStream$ = this.userStreamSub.pipe(shareReplay(1));
  userStreamGenerating = false;

  selfStream?: MediaStream;
  selfStreamSub = new Subject<MediaStream | undefined>();
  selfStream$ = this.selfStreamSub.pipe(shareReplay(1));
  selfStreamGenerating = false;

  // Screen share related properties
  screenShare = false;
  screenShareSub = new BehaviorSubject<boolean>(this.screenShare);
  screenShare$ = this.screenShareSub.asObservable();
  screenShareStreamGenerating = false;
  screenShareAudioContext?: AudioContext;
  screenShareCanceledSub = new Subject<boolean>();
  screenShareCanceled$ = this.screenShareCanceledSub.asObservable();

  // Broacast stream: local stream to be published to red5pro by admins
  broadcastStream?: MediaStream;
  broadcastStreamSub =  new Subject<MediaStream | undefined>();
  broadcastStream$ = this.broadcastStreamSub.asObservable();
  broadcastStreamGenerating = false;

  // Track usage of the service to determine if we need to clean up
  usages = 0;
  usages$ = new Subject<number>();

  // Track details of the currently active area to specify the media constraints
  activeArea?: Area;
  activeArea$ = this.activeDataService.activeArea$;

  constructor(
    private mediaDevicesService: MediaDevicesService,
    private activeDataService: ActiveDataService
  ) {
    // Setup default values for local storage based items
    localStorage.setItem('videoStatus', this.videoStatus ? 'true' : 'false');
    localStorage.setItem('audioStatus', this.audioStatus ? 'true' : 'false');
    localStorage.setItem('dndStatus', this.dndStatus ? 'true' : 'false');
  }

  /**
   * Intialise the user's streams once area data is available
   */
  initStreams() {
    // Already inited, so do nothing
    if (this.userStream) {
      return;
    }
    // Once we recieve the area data (one time)
    this.activeArea$.pipe(first(area => area !== undefined)).subscribe(async (area) => {
      // Determine available devices (media permissions granted via area guard)
      await this.mediaDevicesService.devicesAvailable();
      this.activeArea = area;
      this.generateUserStream();
      // Generate the user stream and respond to changes of the chosen video / audio devices
      this.mediaDevicesService.videoDeviceId$.pipe(skip(1)).subscribe(() => this.refreshStreams());
      this.mediaDevicesService.audioDeviceId$.pipe(skip(1)).subscribe(() => this.refreshStreams());
      // When the user stream changes, we need to update the self stream
      this.userStream$.subscribe(this.generateSelfStream.bind(this));
    });
    // If something about the active area has changed, we need to re-generate the user stream
    this.activeArea$.subscribe((area) => {
      if (this.activeArea && this.activeArea !== area) {
        this.activeArea = area;
        this.generateUserStream();
      }
    });
    // Track usage of media streams, once they are no longer needed we can stop the streams
    this.usages$.subscribe((usages) => {
      if (usages == 0) {
        this.stopStreams();
      }
      // If the streams are stopped, we need to generate them again
      else if (usages > 0 && this.activeArea) {
        if (!this.userStream) {
          this.generateUserStream();
        }
      }
    });
  }

  /**
   * Trigger a refresh of the main user stream, which will in turn refresh the 'self preview' stream.
   * This will also handle switching from a screen share stream to a camera stream when required
   */
  refreshStreams() {
    // If a screen share was previously active it needs to be stopped
    if (this.userStream && this.screenShare) {
      if (this.screenShareAudioContext) {
        this.screenShareAudioContext.close();
      }
      this.stopStreams();
      this.setScreenShare(false);
    }
    this.generateUserStream();
  }

  /**
   * Create the stream shown to the user in self preview components. Requires the main userStream to be present.
   */
  async generateSelfStream() {
    // If the user stream has ended, also end the self stream
    if (!this.userStream) {
      this.suspendSelfVideo();
      return;
    }
    if (this.selfStreamGenerating) {
      return;
    }
    this.selfStreamGenerating = true;
    // We want a heavily scaled / constrained video for our self preview
    // and we don't have a need for audio
    const constraints = this.getDefaultConstraints(true, false);
    constraints.video.width = { ideal: 272 };
    const clonedStream = this.userStream?.clone();
    // Remove the audio tracks
    const audioTracks = clonedStream?.getAudioTracks();
    if (audioTracks) {
      for (const audioTrack of audioTracks) {
        clonedStream?.removeTrack(audioTrack);
      }
    }
    // Resize the existing video track to the new constraints
    const videoTrack = clonedStream?.getVideoTracks()[0];
    videoTrack?.applyConstraints(constraints as MediaTrackConstraints);
    this.selfStream = clonedStream;
    this.selfStreamSub.next(clonedStream);
    this.selfStreamGenerating = false;
    this.toggleSelfVideo(this.videoStatus);
  }

  /**
   * Returns a value when user streams become available, starts creation if not ready
   */
  async streamsAvailable() {
    if (!this.userStream) {
      this.initStreams();
      await firstValueFrom(this.userStream$);
    }
  }

  suspendSelfVideo() {
    this.selfStream?.getTracks().forEach((track) => {
      // 'stop'ing each track is required to actually de-activate the hardware / stream from device
      track.stop();
    });
    this.selfStream = undefined;
    this.selfStreamSub.next(undefined);
  }

  /**
   * Generates the 'userStream' that will be sent to other room members
   */
  async generateUserStream() {
    // Screen shares are handled differently
    if (this.mediaDevicesService.videoDeviceId == 'screen-share') {
      this.generateScreenShareStream();
      return;
    }
    if (this.userStreamGenerating) {
      return;
    }
    this.userStreamGenerating = true;
    // Each area can have it's own constraints that we need to apply
    const constraints = this.getDefaultConstraints();
    constraints.video.height = { ideal: this.activeArea?.localStreamHeight ?? 160 };
    // @TODO apply additional area constraints using RTC sender (i.e: bitrate)
    // @TODO potentially apply a set of constraints based on the CSS layout
    // and then apply a different set of constraints when the user is fullscreened
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    this.userStream = stream;
    this.userStreamGenerating = false;
    // Set the current mute / camera enabled status for the returned streams
    this.toggleUserVideo(this.videoStatus);
    this.toggleAudioStatus(this.audioStatus);
    // Inform subscribers of the new stream
    this.userStreamSub.next(stream);
  }

  async generateScreenShareStream(): Promise<boolean> {
    if (!this.mediaDevicesService.screenShareSupported()) {
      this.mediaDevicesService.showError('Screen sharing / display capture is not supported on this device');
    }
    if (this.userStreamGenerating || this.screenShareStreamGenerating) {
      return false;
    }
    this.userStreamGenerating = true;
    this.screenShareStreamGenerating = true;
    // generate the display capture stream, but also handle when user rejects/cancels
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia(this.mediaDevicesService.displayMediaOptions);
      this.screenShare = true;
      this.screenShareSub.next(true);
      // We need to create a seperate stream containing only the
      // audio from the chosen device
      const audioConstraints = this.getDefaultConstraints(false).audio;
      const audioStream = await navigator.mediaDevices.getUserMedia({audio: audioConstraints});
      if (this.screenShareAudioContext) {
        this.screenShareAudioContext.close();
      }
      // If the user has chosen to also share screen audio
      // use the web audio API to merge to two audio streams into one
      // as playing multiple tracks via a single <video> or <audio>
      // is not supported
      if (stream.getAudioTracks().length) {
        this.screenShareAudioContext = new AudioContext();
        const audioDest = this.screenShareAudioContext.createMediaStreamDestination();
        const shareAudio = this.screenShareAudioContext.createMediaStreamSource(stream);
        const deviceAudio = this.screenShareAudioContext.createMediaStreamSource(audioStream);
        shareAudio.connect(audioDest);
        deviceAudio.connect(audioDest);
        const mixedStream = audioDest.stream;
        // Remove the audio track from the screen share stream and replace it with the mixed stream
        stream.removeTrack(stream.getAudioTracks()[0]);
        stream.addTrack(mixedStream.getAudioTracks()[0]);
        // Stop the stream we generated for the purpose of mixing
        mixedStream.removeTrack(mixedStream.getAudioTracks()[0]);
      }
      // If not just insert the track regularly
      else {
        stream.addTrack(audioStream.getAudioTracks()[0]);
      }
      // Stop the stream we generated from the audio device
      audioStream.removeTrack(audioStream.getAudioTracks()[0]);
      // Stop the current streams (there should always be a stream)
      this.stopStreams();
      this.userStream = stream;
      this.userStreamSub.next(stream);
      this.setScreenShare(true);
      // Set the current mute / camera enabled status for the returned streams
      this.toggleUserVideo(this.videoStatus);
      this.toggleAudioStatus(this.audioStatus);
      this.userStreamGenerating = false;
      this.screenShareStreamGenerating = false;
      return true;
    }
    catch(error: any) {
      this.userStreamGenerating = false;
      this.screenShareStreamGenerating = false;
      this.screenShareCanceledSub.next(true);
      console.error(`${error.name}: ${error.message}`);
      return false;
    }
  }

  // Make a media stream using the user's currently chosen devices intended to be broadcast to red5pro
  async generateBroadcastStream() {
    if (this.broadcastStream || this.broadcastStreamGenerating) {
      return;
    }
    this.broadcastStreamGenerating = true;
    let stream: MediaStream;
    // If the user is screen sharing, we want to broadcast that
    console.log('screenshare?', this.screenShare);
    if (this.screenShare && this.userStream) {
      stream = this.userStream;
    }
    else {
      // For the broadcast stream we want better quality
      // Cloning an existing stream and applying new 'higher' constraints is supposed to work, but doesn't currently
      // you just get a low quality stream or an 'overconstrained' error
      const constraints = this.getDefaultConstraints();
      // @TODO: get quality from area settings?
      if (constraints.video) {
        constraints.video.height = { ideal: 720 };
      }
      console.log(constraints);
      stream = await navigator.mediaDevices.getUserMedia(constraints);
    }
    this.broadcastStream = stream;
    this.broadcastStreamSub.next(stream);
    this.broadcastStreamGenerating = false;
    // Set the current mute / camera enabled status for the returned streams
    this.toggleUserVideo(this.videoStatus);
    this.toggleAudioStatus(this.audioStatus);
  }

  // Return a common set of constraints with the current devices specified. Intended to be overriden as needed.
  getDefaultConstraints(video = true, audio = true) {
    const constraints: MediaStreamConstraints = { video: false, audio: false};
    if (video) {
      constraints.video = {
        // Let the client try and scale to the desired ratio
        aspectRatio: 16 / 9,
        // @ts-ignore (this property is valid, not sure why it's not in the type yet)
        resizeMode: 'crop-and-scale',
        // We can also specify FPS to handle bandwidth limited scenarios
        frameRate: { ideal: 24 },
        deviceId: { exact: this.mediaDevicesService.videoDeviceId }
      }
    }
    if (audio) {
      constraints.audio = {
        deviceId: { exact: this.mediaDevicesService.audioDeviceId },
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      };
    }
    return constraints as Record<'video' | 'audio', MediaTrackConstraints>;
  }

  toggleAudioStatus(status?: boolean) {
    // If no status is provided, toggle the current status
    const value = typeof status !== 'undefined' ? status : !this.audioStatus;
    // Disable audio tracks on all needed streams
    const streams = [this.userStream, this.broadcastStream];
    streams.forEach((stream) => {
      stream?.getAudioTracks().forEach((track) => track.enabled = value);
    });
    this.setAudioStatus(value);
  }

  toggleVideoStatus(status?: boolean) {
    const value = typeof status !== 'undefined' ? status : !this.videoStatus;
    this.toggleUserVideo(value);
    this.toggleSelfVideo(value);
    this.toggleBroadcastVideo(value);
    this.setVideoStatus(value);
  }

  toggleVideoTracks(status: boolean) {
    const streams = [this.userStream, this.selfStream, this.broadcastStream];
    streams.forEach((stream) => {
      stream?.getAudioTracks().forEach((track) => track.enabled = status);
    })
  }

  toggleDoNotDisturb(status?: boolean) {
    const value = typeof status !== 'undefined' ? status : !this.dndStatus;
    this.toggleAudioStatus(!value);
    this.toggleVideoStatus(!value);
    this.setDndStatus(value);
  }

  toggleUserVideo(enabled: boolean) {
    this.userStream?.getVideoTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  toggleSelfVideo(enabled: boolean) {
    this.selfStream?.getVideoTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  toggleBroadcastVideo(enabled: boolean) {
    this.broadcastStream?.getVideoTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  stopUserStream() {
    this.userStream?.getTracks().forEach((track) => {
      track.stop();
    });
    this.userStream = undefined;
    this.userStreamSub.next(undefined);
  }

  setVideoStatus(value: boolean) {
    if (value !== this.videoStatus) {
      localStorage.setItem('videoStatus', value ? 'true' : 'false');
      this.videoStatus = value;
      this.videoStatusSub.next(value);
    }
  }

  setAudioStatus(value: boolean) {
    if (value !== this.audioStatus) {
      localStorage.setItem('audioStatus', value ? 'true' : 'false');
      this.audioStatus = value;
      this.audioStatusSub.next(value);
    }
  }

  setDndStatus(value: boolean) {
    if (value !== this.dndStatus) {
      localStorage.setItem('dndStatus', value ? 'true' : 'false');
      this.dndStatus = value;
      this.dndStatusSub.next(value);
    }
  }

  setAdminMuted(value: boolean) {
    if (value !== this.adminMuted) {
      this.adminMuted = value;
      this.adminMutedSub.next(value);
      // Always mute the user, as even if they are unmuted by an admin we want them to be muted by default
      this.setAudioStatus(false);
    }
  }

  setScreenShare(value: boolean) {
    if (value !== this.screenShare) {
      this.screenShare = value;
      this.screenShareSub.next(value);
    }
  }

  registerUsage() {
    this.usages++;
    this.usages$.next(this.usages);
  }

  unregisterUsage() {
    this.usages--;
    this.usages$.next(this.usages);
  }

  stopStreams(): void {
    this.stopUserStream();
    //this.suspendSelfVideo();
  }

}
